import React, { Dispatch, SetStateAction, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormDateField,
  FormField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { stripTimeFromFormat } from '@/utils/date'
import { AffiliateDto } from 'api/Affiliate/AffiliateDto'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { useAppSelector } from 'store'
import { hasPermission } from 'utils/permissionsValidator'
import { rawDateString, zonedTimeToUtc } from 'utils/time-zone/helpers'

import ClientAreaAuditLogModal from '../../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { AuditLogIcon } from '../../../../../icons/AuditLogIcon'
import { AffiliateNavigation } from '../AffiliateNavigations'

const AffiliateDetailsFormUI: React.FC<FormikProps<AffiliateDetailsFormValues> & OuterProps> = (
  props
) => {
  const countries = useAppSelector((state) => state.identity.countries) ?? []

  const {
    setFieldValue,
    values,
    resetForm,
    submitForm,
    permissions,
    errors,
    onSetWorldCheckModal,
    onSetWorldCheckHistoryModal,
  } = props

  const [isEdit, setEdit] = useState<boolean>(false)
  const [auditLogModal, setAuditLogModal] = useState<{ visible: boolean; data?: AffiliateDto }>({
    visible: false,
  })

  const dateFormatType = props.dateFormatType ?? 'dd/MM/yyyy'

  const handleSave = () => {
    submitForm()
  }

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleWorldCheck = () => {
    onSetWorldCheckModal({ visible: true, enableOngoingScreening: false })
  }

  const setAuditLog = (data: AffiliateDto) => {
    setAuditLogModal({ data: data, visible: true })
  }

  return (
    <React.Fragment>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          affiliateId={auditLogModal.data.id}
        />
      )}

      <AffiliateNavigation data={props.data} isEdit={isEdit} setEdit={setEdit} />

      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {isEdit ? (
                <>
                  <Button
                    type='button'
                    variant='outline'
                    disabled={props.isSubmitting}
                    onClick={handleCancel}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    disabled={props.isSubmitting}
                    onClick={handleSave}
                    data-test='save-button'
                  >
                    Save
                    {props.isSubmitting && <Spinner data-icon='inline-start' />}
                  </Button>
                </>
              ) : (
                <Button
                  type='button'
                  disabled={!hasPermission(permissions, PermissionsEnum.CanEditAffiliates)}
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                >
                  Edit
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      type='button'
                      data-test='more-button'
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      disabled={
                        !hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening) &&
                        !hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults)
                      }
                    >
                      WorldCheck
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        disabled={
                          !hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening)
                        }
                        onSelect={handleWorldCheck}
                      >
                        Screening
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={
                          !hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults)
                        }
                        onSelect={() => onSetWorldCheckHistoryModal(true)}
                      >
                        History
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuItem
                    onSelect={() => setAuditLog(props.data)}
                    data-test='audit-log-menu-button'
                  >
                    <AuditLogIcon style={{ width: 12, height: 12, cursor: 'pointer' }} /> Audit Log
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard columns={2} title='Main'>
              <FormField
                label='First Name'
                name='firstname'
                readOnly={!isEdit}
                dataTest='first-name-field'
                error={errors.firstname}
              />

              <FormField
                label='Last Name'
                name='lastname'
                readOnly={!isEdit}
                dataTest='last-name-field'
                error={errors.lastname}
              />

              <FormDateField
                name='birthday'
                label='Birthday'
                dateFormatType={stripTimeFromFormat(dateFormatType)}
                readOnly={!isEdit}
                error={errors.birthday}
                onChange={(value) =>
                  value &&
                  setFieldValue('birthday', rawDateString(zonedTimeToUtc(value).toISOString()))
                }
              />

              <FormSelectField
                options={countries}
                onChange={(value) => setFieldValue('nationalityId', value ? String(value) : '')}
                label='Nationality'
                name='nationalityId'
                readOnly={!isEdit}
                showSearch
                error={errors.nationalityId}
                dataTest='nationality-field'
              />

              <FormReadonlyField
                label='Account Type'
                name='affiliateTypeName'
                value={values.affiliateTypeName}
                dataTest='affiliate-type-field'
              />
            </DataCard>
          </DataColumn>

          <DataColumn>
            <DataCard title='Contact'>
              <FormReadonlyField
                label='Phone Number'
                name='phoneNumber'
                value={values.phoneNumber}
                dataTest='phone-number-field'
              />
              <FormReadonlyField
                label='Email Address'
                name='email'
                value={values.email}
                dataTest='email-address-field'
              />
            </DataCard>

            <DataCard columns={2} title='Address'>
              <FormReadonlyField
                label='Street'
                name='street'
                value={values.street}
                dataTest='street-field'
              />
              <FormReadonlyField
                label='City'
                name='city'
                value={values.city}
                dataTest='city-field'
              />
              <FormSelectField
                options={countries}
                onChange={(value) => setFieldValue('countryId', value ? String(value) : '')}
                label='Country'
                name='countryId'
                readOnly={!isEdit}
                data-test='country-field'
              />

              <FormReadonlyField
                label='State'
                name='state'
                value={values.state}
                dataTest='state-field'
              />
              <FormReadonlyField
                label='Postal Code'
                name='zipCode'
                value={values.zipCode}
                dataTest='postal-code-field'
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </React.Fragment>
  )
}

export interface AffiliateDetailsFormValues {
  firstname: string
  lastname: string
  birthday: string
  nationalityId: string
  countryId: string
  affiliateTypeName: string
  phoneNumber: string
  email: string
  street: string
  city: string
  state: string
  zipCode: string
}

interface OuterProps {
  data: AffiliateDto
  dateFormatType: string | undefined

  permissions: PermissionDto[]

  onSubmit(id: string, data: AffiliateUpdateDto): void

  onSetWorldCheckModal({
    visible,
    enableOngoingScreening,
  }: {
    visible: boolean
    enableOngoingScreening: boolean
  }): void

  onSetWorldCheckHistoryModal: Dispatch<SetStateAction<boolean>>
}

export interface AffiliateUpdateDto {
  firstname: string
  lastname: string
  birthday: string
  nationalityId: string
  countryId: string
}

const prepareUpdateData = (values: AffiliateDetailsFormValues): AffiliateUpdateDto => {
  return {
    firstname: values.firstname,
    lastname: values.lastname,
    birthday: values.birthday,
    nationalityId: values.nationalityId,
    countryId: values.countryId,
  }
}

export const AffiliateDetailsForm = withFormik<OuterProps, AffiliateDetailsFormValues>({
  mapPropsToValues: ({ data }) => ({
    ...data,
    affiliateTypeName: data.affiliateType.name,
    countryId: data?.country?.id ?? '',
    nationalityId: data?.nationality?.id ?? '',
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(props.data.id, prepareUpdateData(values))
      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  },
  validate(values) {
    const errors: FormikErrors<AffiliateDetailsFormValues> = {}
    if (!values.birthday) {
      errors.birthday = 'Required'
    }
    if (!values.countryId) {
      errors.countryId = 'Required'
    }
    if (!values.nationalityId) {
      errors.nationalityId = 'Required'
    }
    if (!values.firstname) {
      errors.firstname = 'Required'
    }
    if (!values.lastname) {
      errors.lastname = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
})(AffiliateDetailsFormUI)
